// @flow
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {Grid, Hidden} from '@material-ui/core';
import {Redirect, useLocation} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import FloatingBottom from '../../configure/common/components/FloatingBottom';
import {usePage} from '../../page/utils';
import tiendada from '../../../assets/img/tiendada.png';
import onboarding from '../../../assets/img/onboarding.png';
import backgroundOnboarding from '../../../assets/img/backgroundOnboarding.png';

import {ProgressBar} from '../../../elements/ProgressBar/ProgressBar';
import TierComparison from '../tier-comparison/TierComparison';

import type {Country, Currency, User} from '../../../entities';

type Props = {|
  user?: User,
  initialAuthDone?: boolean,
  currencies?: Currency[],
  isFetchingCurrencies?: boolean,
  countries?: Country[],
  isFetchingCountries?: boolean,
  errorOnboarding?: string,
  error?: string,
|};

const useStyles = makeStyles(theme => ({
  mainWrap: {
    padding: '1rem',
  },
  logo: {
    height: 48,
    width: 'auto',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    textAlign: 'center',
  },
  progressWrap: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  mobileProgressBar: {
    width: '100%',
  },
  progress: {
    width: '100%',
    marginRight: '8px',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
  },
  onBoardingHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  progressWrapHeader: {
    width: '50%',
  },
  form: {
    width: '100%',
    backgroundColor: '#E7F1FD',
    boxSizing: 'border-box',
    padding: theme.spacing(4),
    textAlign: 'center',
    borderRadius: theme.borderRadius,
    maxWidth: 440,
    [theme.breakpoints.down('xs')]: {
      maxWidth: 328,
    },
  },
  wrapperContainer: {
    background: 'white',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      paddingBottom: '200px',
    },
  },
  inputForm: {
    marginBottom: theme.spacing(3),
  },
  innerInputForm: {
    background: 'white',
    textAlign: 'start',
  },
  bigTitle: {
    marginBottom: theme.spacing(4),
    fontWeight: 600,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'unset',
    },
  },
  text: {
    maxWidth: 440,
    marginBottom: theme.spacing(4),
  },
  submit: {
    margin: theme.spacing(1, 0, 2),
    fontWeight: 600,
  },
  errorText: {
    marginBottom: theme.spacing(2),
  },
  loading: {
    position: 'absolute',
  },
  buttonWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '.5rem',
  },
  divider: {
    border: '1px solid #E8E8ED',
    margin: '16px auto',
  },
  leftSide: {
    padding: '0 .5rem',
    margin: 'auto',
    maxWidth: '1200px',
  },
  imageSlider: {
    float: 'left',
    height: 'auto',
    padding: '16px',
    paddingBottom: 0,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      width: 'auto',
      margin: 'auto',
      padding: '32px',
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
  backgroundRightSide: {
    overflow: 'hidden',
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    maxHeight: '300px',
    [theme.breakpoints.up('sm')]: {
      maxHeight: '500px',
    },
    [theme.breakpoints.up('md')]: {
      minHeight: '100vh',
      height: '100%',
      maxHeight: '100%',
    },
    backgroundImage: `url(${backgroundOnboarding})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
  },

  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
    width: '100%',
  },
  fieldSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  circle: {
    cursor: 'pointer',
    width: '10px',
    height: '10px',
    background: '#D4D4DD',
    borderRadius: '50%',
    margin: '6px',
  },
  circleActive: {
    background: '#717190',
  },
  stepsCircleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 'auto',
    marginTop: '32px',
  },
  subtitleB: {
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '24px',
    color: 'white',
    textAlign: 'center',
    marginTop: '16px',
  },
  titleB: {
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '44px',
    lineHeight: '58px',
    color: 'white',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      marginTop: '48px',
    },
  },
  stickyImgWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

type PlanSelected = 'basic' | 'pro' | null;

export default usePage<Props>(OnBoardingTrial, 'On Boarding trial');

function OnBoardingTrial(props: Props) {
  const {
    user,
    initialAuthDone,
    currencies,
    isFetchingCurrencies,
    countries,
    isFetchingCountries,
  } = props;

  const classes = useStyles();

  const location = useLocation();
  const queryParams = queryString.parse(location.search);

  const [planSelected, setPlanSelected] = useState<PlanSelected>(
    // $FlowFixMe
    queryParams.plan
  );

  const percentage = 80;
  const rateCompleted = `${percentage}%`;

  useEffect(() => {
    if (['basic', 'pro'].includes(queryParams?.plan))
      // $FlowFixMe
      setPlanSelected(queryParams.plan);
    else setPlanSelected(null);
  }, [queryParams]);

  if (isFetchingCountries || isFetchingCurrencies) return null;
  if (!countries || !currencies)
    return <>Ocurrió un error =(, por favor refresca la página</>;

  return (
    <>
      <Grid container className={classes.wrapperContainer}>
        <Grid
          item
          container
          xs={12}
          sm={12}
          md={7}
          className={classes.leftSide}
        >
          <Hidden smDown>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <header className={classes.onBoardingHeader}>
                <div style={{width: 'fit-content', marginTop: '32px'}}>
                  <img alt="tiendada" src={tiendada} />
                </div>

                <Hidden smDown>
                  <div className={classes.progressWrapHeader}>
                    <ProgressBar
                      value={percentage}
                      rateCompleted={rateCompleted}
                    />
                  </div>
                </Hidden>
              </header>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <hr className={classes.divider} />
            </Grid>
          </Hidden>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <div className={classes.fieldsContainer}>
              <TierComparison />
            </div>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={5} style={{backgroundColor: '#6425E4'}}>
          <div className={classes.backgroundRightSide}>
            <Hidden smDown>
              <div style={{padding: '16px'}}>
                <div className={classes.titleB}>¡Empieza a vender!</div>
              </div>
            </Hidden>
            <Hidden smDown>
              <div className={classes.stickyImgWrap}>
                <div
                  style={{
                    height: 'fit-content',
                    position: 'fixed',
                    bottom: '0',
                  }}
                >
                  <img
                    alt="demo"
                    src={onboarding}
                    className={classes.imageSlider}
                  />
                </div>
              </div>
            </Hidden>
          </div>
        </Grid>
      </Grid>
      {!planSelected && (
        <FloatingBottom>
          <div className={classes.mainWrap}>
            {initialAuthDone && !user && <Redirect to={'/login'} />}
            <div className={classes.progressWrap}>
              <ProgressBar
                value={percentage}
                rateCompleted={rateCompleted}
                className={classes.mobileProgressBar}
              />
            </div>
          </div>
        </FloatingBottom>
      )}
    </>
  );
}

OnBoardingTrial.propTypes = {
  user: PropTypes.object,
  initialAuthDone: PropTypes.bool,
  isFetchingCountries: PropTypes.bool,
  isFetchingCurrencies: PropTypes.bool,
  currencies: PropTypes.array,
  countries: PropTypes.array,
  error: PropTypes.string,
  errorOnboarding: PropTypes.string,
};
